import { z } from 'zod';
import { PEOC_DUTY_OFFICER_PHONE } from './emergency-declaration';

/**
 * Public-alert REQUEST + routing to the PEOC (Declaration & Notification Slice 2,
 * #497, migration 0125).
 *
 * In Ontario a public alert (Alert Ready / NAAD / NPAS) is ISSUED only by the PEOC
 * (Provincial Emergency Operations Centre) — Ontario PERP §6. A municipal or First-Nation
 * seat does NOT issue an alert; it REQUESTS one, routed TO the PEOC. This is that request's
 * home: an operator-authored REQUEST record the COP captures ON BEHALF OF the requesting
 * authority and routes to the PEOC via the #490 delivery bridge — records-not-confers, the
 * COP records the request; it does NOT issue the alert. "Request public alert" is recorded
 * in docs/ontario-ims-doctrine-reference.md §4.3 as a CORRECT-DOMAIN-VERBATIM non-IMS term.
 *
 * LEGAL MODEL (encoded so a generic COP can't get it wrong):
 *  - Only the PEOC ISSUES a public alert (PERP §6). The seat REQUESTS. NO seat affordance
 *    says "issue / send / broadcast" a public alert — ever.
 *  - BOTH the municipal and First-Nation paths are wired (unlike declaration Slice 1). A
 *    First-Nation request is ALSO a request TO the PEOC — never a direct NAAD/NPAS issuer
 *    (that is #461, a named scope-out). The FN branch does NOT carry a single-head
 *    (Head of Council) affordance — a First Nation requests via its Band Council.
 *  - A public-alert request, a declaration, and an evacuation order are PARALLEL SIBLINGS.
 *    A request is NOT required for either; do not model it as a parent.
 *
 * FOREIGN-VALUE GUARD: the request's hazard/incident reference renders ONLY the COP's OWN
 * doctrine-safe identity — the linked incident's incident_number + the doctrine-safe
 * HAZARD_LABEL map. NO raw CAP field (event / eventCode / severity / category / area_desc)
 * or NPAS/NAAD internal identifier ever renders on the request surface.
 */

const UUID = z.string().uuid();

/** The KIND of requesting authority. BOTH paths are wired in Slice 2. */
export const PUBLIC_ALERT_AUTHORITY_KINDS = ['municipal', 'first_nation'] as const;
export const PublicAlertAuthorityKindSchema = z.enum(PUBLIC_ALERT_AUTHORITY_KINDS);
export type PublicAlertAuthorityKind = z.infer<typeof PublicAlertAuthorityKindSchema>;

/** Operator-facing labels for the requesting-authority-kind. */
export const PUBLIC_ALERT_AUTHORITY_KIND_LABELS: Record<PublicAlertAuthorityKind, string> = {
  municipal: 'Municipality',
  first_nation: 'First Nation',
};

/** The named-requester ROLE per authority_kind (rendered on the surface + PDF). The FN branch
 *  is the Band Council (a body), NOT a single head — the municipal/single-head affordance is
 *  deliberately NOT copied onto it. */
export const PUBLIC_ALERT_REQUESTER_ROLE: Record<PublicAlertAuthorityKind, string> = {
  municipal: 'Municipal official',
  first_nation: 'Band Council',
};

/** Lifecycle: 'active' (request stands) | 'rescinded' (withdrawn). */
export const PublicAlertRequestStatusSchema = z.enum(['active', 'rescinded']);
export type PublicAlertRequestStatus = z.infer<typeof PublicAlertRequestStatusSchema>;

/**
 * Operator-facing labels — doctrine-strict (per-surface rule §4.1: UI = strict verbatim).
 * "Request public alert" (§4.3 correct-domain-verbatim) — a REQUEST routed TO the PEOC, NOT
 * an issuance. The seat NEVER says "issue / send / broadcast" a public alert (PERP §6 — only
 * the PEOC issues). Pinned by public-alert-request.lock.test.ts (editing them is founder
 * authorization).
 */
export const REQUEST_PUBLIC_ALERT_LABEL = 'Request public alert';
export const RESCIND_PUBLIC_ALERT_REQUEST_LABEL = 'Rescind public alert request';
export const PUBLIC_ALERT_REQUEST_HEADING = 'Public Alert Request';

/**
 * #461 — the intro shown when the public-alert REQUEST is raised FROM an evacuation order
 * (the evac-order → public-alert bridge). records-not-confers, PERP §6: a public alert is
 * issued ONLY by the PEOC; the seat REQUESTS one and the request inherits the order's issuing
 * authority. DELIBERATELY phrased so no issuance verb (issue/send/broadcast/relay) precedes
 * "public alert" and so it never reads as "alert the public" — the seat never claims issuance.
 * Pinned by public-alert-request.lock.test.ts (editing it is founder authorization).
 */
export const PUBLIC_ALERT_REQUEST_FROM_EVAC_NOTE =
  'A public alert is issued only by the PEOC (PERP §6). Requesting one here records the request on behalf of this order’s issuing authority and routes it to the PEOC — it does not issue the alert.';

/**
 * The PERP §6 records-not-confers copy the request surface states. The issuer_note attributes
 * issuance to the PEOC (never the seat); the request_note states records-not-confers; the
 * live_route names the method-agnostic PEOC Duty Officer route. Deliberately phrased so no
 * clause puts "issue/send/broadcast" before "public alert" (the seat never claims issuance).
 */
export const PUBLIC_ALERT_REQUEST_PEOC_COPY = {
  issuer_note: 'A public alert is issued only by the PEOC (Ontario PERP §6).',
  request_note:
    'This records a request to the PEOC on behalf of the authority; it does not issue the alert.',
  live_route: 'routed to the PEOC Duty Officer for issuance consideration',
} as const;

/** Re-export the seeded PEOC Duty-Officer 24/7 line (migration 0124) for the routing surface. */
export { PEOC_DUTY_OFFICER_PHONE };

/* ------------------------------------------------------------------ *
 * Request — POST /api/public-alert-requests (routes to the PEOC)
 * ------------------------------------------------------------------ */

export const PublicAlertRequestCreateSchema = z.object({
  session_id: z.string().min(1).max(128),
  /** BOTH 'municipal' and 'first_nation' are accepted (each is a request TO the PEOC). */
  authority_kind: PublicAlertAuthorityKindSchema.default('municipal'),
  /** The requesting jurisdiction — a REAL place (municipality / First Nation name). */
  requesting_authority_name: z.string().min(1).max(200),
  /** The named requester — optional free text. NOT hard-coded to a single head. */
  requester_name: z.string().max(200).optional(),
  /** Optional PARALLEL-SIBLING context link to the hazard/incident (never a precondition). */
  incident_id: UUID.optional(),
  /** #461 — provenance: the evacuation order this request was raised FROM (the evac-order →
   *  public-alert bridge). When present the route inherits the order's AREA (geography) and
   *  links the request back to it. Optional (the incident-block path has none). */
  evacuation_order_id: UUID.optional(),
  /** The COP's OWN hazard enum — never a raw CAP field. */
  hazard_type: z.string().max(80).optional(),
  notes: z.string().max(2000).optional(),
  /** Explicit registry destination id; omitted → the default (PEOC) destination. */
  destination_id: UUID.optional(),
  /** The operator confirms they also placed the PEOC Duty-Officer phone call to flag the request. */
  call_confirmed: z.boolean().optional().default(false),
  call_notes: z.string().max(2000).optional(),
});
export type PublicAlertRequestCreate = z.infer<typeof PublicAlertRequestCreateSchema>;

/** POST /api/public-alert-requests/:id/rescind — flip active → rescinded + route a withdrawal. */
export const PublicAlertRequestRescindSchema = z.object({
  session_id: z.string().min(1).max(128),
  reason: z.string().max(2000).optional(),
  /** Explicit registry destination id for the withdrawal notice; omitted → default (PEOC). */
  destination_id: UUID.optional(),
});
export type PublicAlertRequestRescind = z.infer<typeof PublicAlertRequestRescindSchema>;

/* ------------------------------------------------------------------ *
 * Read shape — a plain record (geography is optional; not served as GeoJSON in Slice 2)
 * ------------------------------------------------------------------ */

export const PublicAlertRequestRecordSchema = z.object({
  id: z.string(),
  request_number: z.string(),
  authority_kind: PublicAlertAuthorityKindSchema,
  requesting_authority_name: z.string(),
  requester_name: z.string().nullable(),
  incident_id: z.string().nullable(),
  /** #461 — the evacuation order this request was raised FROM (provenance link, nullable). */
  evacuation_order_id: z.string().nullable(),
  /** The COP's OWN hazard enum — the surface maps it through HAZARD_LABEL (never raw CAP). */
  hazard_type: z.string().nullable(),
  status: PublicAlertRequestStatusSchema,
  requested_at: z.string(),
  rescinded_at: z.string().nullable(),
  rescind_reason: z.string().nullable(),
  peoc_notified_at: z.string().nullable(),
  notes: z.string().nullable(),
  /** The recorder (the operator who captured it in-COP) — ORTHOGONAL to the requester. */
  created_by_operator_id: z.string().nullable(),
});
export type PublicAlertRequestRecord = z.infer<typeof PublicAlertRequestRecordSchema>;

/** The request/rescind response — the record + the PEOC routing outcome the operator sees. */
export const PublicAlertRequestResultSchema = z.object({
  request: PublicAlertRequestRecordSchema,
  delivery_channel: z.enum(['webhook', 'email', 'console_log']),
  destination_name: z.string().nullable(),
  /** The PEOC Duty-Officer phone the operator should call (from the destination row). */
  contact_phone: z.string().nullable(),
});
export type PublicAlertRequestResult = z.infer<typeof PublicAlertRequestResultSchema>;
